import React, { useState, useEffect, useRef } from 'react';
import DataTable from './DataTable';
import { getSubmissions, resetSubmission } from '../../lib/adminApi';
import { formatToIST } from '../../lib/dateUtils';

function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [resetConfirm, setResetConfirm] = useState(null);
  const [viewScript, setViewScript] = useState(null);
  const [resetting, setResetting] = useState(false);

  // Ref to prevent double API calls in StrictMode
  const hasFetched = useRef(false);

  const fetchSubmissions = async (page = 1) => {
    setLoading(true);
    try {
      const data = await getSubmissions(page, pagination.limit);
      setSubmissions(data.submissions || []);
      setPagination({ page: data.page, limit: data.limit, total: data.total });
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchSubmissions();
  }, []);

  const handleReset = async (userId, questionId) => {
    setResetting(true);
    try {
      await resetSubmission(userId, questionId);
      setResetConfirm(null);
      fetchSubmissions(pagination.page);
    } catch (err) {
      console.error('Failed to reset submission:', err);
    } finally {
      setResetting(false);
    }
  };

  const columns = [
    { key: 'userId', label: 'User ID' },
    { key: 'questionId', label: 'Question ID' },
    { key: 'submissionNo', label: 'Submission #' },
    {
      key: 'submissionTime',
      label: 'Submitted At (IST)',
      render: (val) => formatToIST(val),
    },
    {
      key: 'script',
      label: 'Script',
      render: (val, row) => (
        <button
          onClick={() => setViewScript(row)}
          className="text-blue-400 hover:text-blue-300 text-sm"
        >
          View Code
        </button>
      ),
    },
  ];

  const actions = (row) => (
    <div className="flex gap-2 justify-end">
      <button
        onClick={() => setResetConfirm(row)}
        className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
      >
        Reset
      </button>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Submissions</h1>
      </div>

      <DataTable
        columns={columns}
        data={submissions}
        loading={loading}
        pagination={pagination}
        onPageChange={fetchSubmissions}
        actions={actions}
        emptyMessage="No submissions found."
      />

      {/* View Script Modal */}
      {viewScript && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 border border-gray-700 max-h-[80vh] flex flex-col">
            <h2 className="text-xl font-bold text-white mb-4">
              Submission Script (User: {viewScript.userId}, Question: {viewScript.questionId})
            </h2>
            <pre className="flex-1 overflow-auto bg-gray-900 p-4 rounded text-sm text-gray-300 font-mono">
              {viewScript.script || 'No script content'}
            </pre>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setViewScript(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Confirm Reset</h2>
            <p className="text-gray-300 mb-4">
              Are you sure you want to reset this submission?
            </p>
            <div className="bg-gray-700/50 rounded p-3 mb-4 text-sm">
              <p className="text-gray-400 mb-1">This will:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>Delete all submissions for User <span className="text-white font-semibold">{resetConfirm.userId}</span> on Question <span className="text-white font-semibold">{resetConfirm.questionId}</span></li>
                <li>Set attempts count to <span className="text-white font-semibold">0</span></li>
                <li>Set solved status to <span className="text-white font-semibold">false</span></li>
              </ul>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setResetConfirm(null)}
                disabled={resetting}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReset(resetConfirm.userId, resetConfirm.questionId)}
                disabled={resetting}
                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {resetting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Resetting...
                  </>
                ) : (
                  'Reset'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSubmissions;
